import classNames from "classnames";
import { useMemo } from "react";
import { BaseButton } from "../../../components/Button/BaseButton.tsx";
import { Button } from "../../../components/Button/Button.tsx";
import { Icon } from "../../../components/Icon/Icon.tsx";
import { trackEvent } from "../../../tracking/tracking.ts";
import { computeDaysUntilDate } from "../../../utils/date.ts";
import { PaymentDialog } from "../../components/PaymentDialog/PaymentDialog.tsx";
import { upgradeDialogStore } from "../../components/UpgradeDialog/upgradeDialogStore.ts";
import { CREDIT_PRICE, WATERMARK_COST } from "../../constants.ts";
import { useCreatePaymentCheckoutUrl } from "../../hooks/useCreatePaymentCheckoutUrl.ts";
import { useWorkspace } from "../../hooks/useWorkspace.ts";
import { CreditsAmountText } from "../components/CreditsAmountText.tsx";
import { GeneratedContentContainer } from "../components/GeneratedContent/GeneratedContentContainer.tsx";
import { VIDEO_RESOURCE_TYPE } from "../constants.ts";
import { useBoardAssets } from "../hooks/useBoardAssets.ts";
import { useDownloadWithWatermark } from "../hooks/useDownloadWithWatermark.ts";
import { usePurchaseAndDownload } from "../hooks/usePurchaseAndDownload.ts";
import type { Content } from "../types.ts";
import { saveShoppingCart } from "../utils/shoppingCart.ts";

export const ContentDownloadDialog = ({
  isOpen,
  onOpenChange,
  assetUuids,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  assetUuids: string[];
}) => {
  const { workspace } = useWorkspace();
  const { assets: boardAssets } = useBoardAssets();

  const contentsToDownload = useMemo(
    () =>
      boardAssets
        ?.filter((asset) => assetUuids.includes(asset.uuid))
        .map((asset) => asset.content) ?? [],
    [boardAssets, assetUuids],
  );

  const purchaseCost = useMemo(
    () =>
      contentsToDownload.reduce(
        (toPurchase, content) =>
          toPurchase + (content.purchased_at === null ? 1 : 0),
        0,
      ) * WATERMARK_COST,
    [contentsToDownload],
  );

  const creditsToBuy = Math.max(
    purchaseCost - workspace.total_credits_remaining_amount,
    0,
  );

  return (
    <PaymentDialog
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      topSectionContent={
        contentsToDownload.length > 0 && (
          <TopSectionContent content={contentsToDownload[0]} />
        )
      }
      midSectionContent={
        <MidSectionContent
          assetUuids={assetUuids}
          contentsToDownload={contentsToDownload}
          purchaseCost={purchaseCost}
          creditsToBuy={creditsToBuy}
          onOpenChange={onOpenChange}
        />
      }
      bottomSectionContent={<BottomSectionContent />}
      displayBottomSection={creditsToBuy > 0}
    />
  );
};

const TopSectionContent = ({ content }: { content: Content }) => (
  <>
    <div className="flex-col items-end gap-400 -rotate-3">
      <div className="h-600 px-200 py-100 rounded-full label-md-semibold text-inverse-primary bg-surface-transparent-inverse-rest">
        Your current asset
      </div>
      <div className="w-[124px] border-100 rounded-150 bg-button-surface-inverse-primary-rest shadow-100">
        <div className="relative">
          <GeneratedContentContainer content={content} />
          <div
            className="absolute inset-0 opacity-50"
            style={{
              backgroundImage:
                "url('https://storage.googleapis.com/419c45cf-be8a-4cba-bbcd-74a221eb2587/app/assets/watermark/watermark_45_degrees.png')",
              backgroundRepeat: "repeat",
              backgroundSize: "50px 50px",
            }}
          />
        </div>
      </div>
    </div>
    <div className="flex-col items-center gap-400 rotate-3">
      <div className="h-600 px-200 py-100 rounded-full label-md-semibold bg-surface-caution-primary-rest">
        What you'll get
      </div>
      <div className="w-[124px] border-100 rounded-150 bg-button-surface-inverse-primary-rest shadow-100">
        <GeneratedContentContainer content={content} />
      </div>
    </div>
  </>
);

const MidSectionContent = ({
  assetUuids,
  contentsToDownload,
  purchaseCost,
  creditsToBuy,
  onOpenChange,
}: {
  assetUuids: string[];
  contentsToDownload: Content[];
  purchaseCost: number;
  creditsToBuy: number;
  onOpenChange: (open: boolean) => void;
}) => {
  const { workspace } = useWorkspace();

  const contentUuids = useMemo(
    () => contentsToDownload.map((content) => content.uuid),
    [contentsToDownload],
  );

  const isAnyContentVideo = useMemo(
    () =>
      contentsToDownload.some(
        (content) => content.resource_type === VIDEO_RESOURCE_TYPE,
      ),
    [contentsToDownload],
  );

  const {
    mutate: purchaseAndDownload,
    isLoading: isLoadingPurchaseAndDownload,
  } = usePurchaseAndDownload({
    assetUuids,
    contentUuids,
  });
  const {
    mutate: downloadWithWatermark,
    isLoading: isDownloadingWithWatermark,
  } = useDownloadWithWatermark();
  const { createPaymentCheckoutUrl, isCreatePaymentCheckoutUrlLoading } =
    useCreatePaymentCheckoutUrl();

  const price = Math.round(CREDIT_PRICE[workspace.type] * creditsToBuy) * 0.01;

  const daysBeforeRenewal = workspace.subscription_credits_next_added_at
    ? computeDaysUntilDate(
        Date.parse(workspace.subscription_credits_next_added_at),
      )
    : undefined;
  const optionalS = assetUuids.length > 1 ? "s" : "";

  return (
    <div className="flex-col gap-600">
      <div className="heading-2xl">
        Download {assetUuids.length} image{optionalS}
      </div>
      <div className="flex-col gap-400">
        <div className="flex-col">
          <div
            className={classNames(
              "flex-row justify-between label-lg-default",
              creditsToBuy > 0 ? "text-error-secondary" : "text-secondary",
            )}
          >
            <div>Current balance</div>
            <CreditsAmountText
              creditsAmount={workspace.total_credits_remaining_amount}
            />
          </div>
          {creditsToBuy > 0 && daysBeforeRenewal !== undefined && (
            <div className="label-lg-default text-secondary">
              Renews in {daysBeforeRenewal} days
            </div>
          )}
        </div>
        <div className="flex-row justify-between label-lg-semibold">
          <div>
            {assetUuids.length}x Watermark-free image{optionalS}
          </div>
          <CreditsAmountText creditsAmount={purchaseCost} />
        </div>
        {creditsToBuy > 0 && (
          <>
            <div className="h-[1px] w-full bg-surface-primary-hover" />
            <div className="flex-col gap-100 p-150 rounded-150 bg-surface-error-secondary-rest">
              <div className="flex-row gap-100 label-lg-default text-error-secondary">
                <div>Insufficient balance, buy</div>
                <CreditsAmountText creditsAmount={creditsToBuy} />
              </div>
              {["pro", "team"].includes(workspace.type) && (
                <div className="label-md-default text-secondary">
                  No immediate payment required: these additional credits will
                  be charged on your next invoice
                </div>
              )}
            </div>
          </>
        )}
      </div>
      <div className="flex-col gap-200">
        <Button
          loading={
            isLoadingPurchaseAndDownload || isCreatePaymentCheckoutUrlLoading
          }
          onClick={() => {
            trackEvent("download_content:without_watermark", {
              content_uuids: contentUuids,
            });
            if (workspace.type === "basic" && creditsToBuy > 0) {
              saveShoppingCart({
                assetUuids,
                contentUuids,
              });
              createPaymentCheckoutUrl({
                workspaceUuid: workspace.uuid,
                contentUuids,
              });
            }
            //  FIXME: We should improve this flow for enterprise accounts (usage based billing...)
            else if (creditsToBuy > 0 && workspace.type === "enterprise") {
              window.open("mailto:contact@pimento.design", "_blank");
            } else {
              purchaseAndDownload();
            }
            onOpenChange(false);
          }}
        >
          {creditsToBuy > 0 ? (
            <div className="flex-row gap-100">
              <div>Buy</div>
              <CreditsAmountText creditsAmount={creditsToBuy} />
              <div>
                and download image{optionalS} (${price.toFixed(2)})
              </div>
            </div>
          ) : (
            <div>Download image{optionalS}</div>
          )}
        </Button>
        <Button
          variant="tertiary"
          loading={isDownloadingWithWatermark}
          // FIXME: Remove this once we support videos with watermark
          disabled={isAnyContentVideo}
          onClick={() => {
            trackEvent("download_content:with_watermark", {
              content_uuids: contentUuids,
            });
            downloadWithWatermark(contentUuids);
            onOpenChange(false);
          }}
        >
          Download with watermark for free
        </Button>
      </div>
    </div>
  );
};

const BottomSectionContent = () => {
  const { workspace } = useWorkspace();

  switch (workspace.type) {
    // FIXME: Improve UI / copy for enterprise account
    case "enterprise":
    case "premium":
    case "team":
      return (
        <>
          <div className="flex-col">
            <div className="label-lg-semibold">Need more out of Pimento?</div>
            <div className="body-md-default">
              We can tailor a plan specifically to your needs.
            </div>
          </div>
          <div className="flex-row items-start">
            <BaseButton
              className="body-md-semibold underline"
              onClick={() => {
                window.open("mailto:contact@pimento.design", "_blank");
              }}
            >
              Contact us
            </BaseButton>
          </div>
        </>
      );
    case "pro":
      return (
        <>
          <div className="flex-col">
            <div className="label-lg-semibold">
              Need more watermark-free images?
            </div>
            <div className="body-md-default">
              Get 2000 monthly credits, 66 image downloads/month, and lower
              single image prices with Pimento Team.
            </div>
          </div>
          <div className="flex-row items-start">
            <Icon name="Sparkles" size="sm" />
            <BaseButton
              className="body-md-semibold underline"
              onClick={() => {
                trackEvent("download_content:upgrade_plan", {
                  workspace_type: workspace.type,
                });
                upgradeDialogStore.openDialog();
              }}
            >
              Upgrade plan
            </BaseButton>
          </div>
        </>
      );
    case "basic":
      return (
        <>
          <div className="flex-col">
            <div className="label-lg-semibold">
              Save up to 50% on image downloads
            </div>
            <div className="body-md-default">
              Get 200 monthly credits, 6 image downloads/month, and lower single
              image prices with Pimento Pro.
            </div>
          </div>
          <div className="flex-row items-start">
            <Icon name="Sparkles" size="sm" />
            <BaseButton
              className="body-md-semibold underline"
              onClick={() => {
                trackEvent("download_content:upgrade_plan", {
                  workspace_type: workspace.type,
                });
                upgradeDialogStore.openDialog();
              }}
            >
              Upgrade plan
            </BaseButton>
          </div>
        </>
      );
  }
};
